import React, { useState, useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Modal, Button, message, Typography, Tooltip, Table } from 'antd';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CsvExport from '@components/CsvExport';
import api from '@services/api';
import EditableTable from '@components/EditableTable';
import FileUploader from '@components/FileUploader';
import { parseCurrency } from '@utils/formatters';
import { getModelData, getModelDataColumns } from '../modelDataInvoice';
import useNavigateToActionsMsg from '../NavigateToActionsMsg';

import * as S from './styles';

const { Text, Paragraph } = Typography;

export interface ItemPayload {
  description: string;
  quantity: number;
  value: number;
  invoiceId: string;
  customerId: string;
  id?: number;
  key?: string;
  totalValue?: number;
}

interface AddItemsToInvoiceProps {
  visible: boolean;
  onClose: () => void;
  invoiceId?: string;
  customerId?: string;
}

const AddItemsToInvoice = ({
  visible,
  onClose,
  invoiceId,
  customerId,
}: AddItemsToInvoiceProps) => {
  const { t } = useTranslation('device-actions-modal');
  const { t: tInvoices } = useTranslation('invoices');
  const [uploadedFile, setUploadedFile] = useState();

  const [items, setItems] = useState<
    | {
        [key: string]: string;
      }[]
    | undefined
  >();
  const [stepModal, setStepModal] = useState('manually');

  const payload = useMemo(
    () =>
      items?.map(item => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { key, totalValue, value, ...newItem } = item;

        return {
          ...newItem,
          value: parseCurrency(value),
          invoiceId: invoiceId ?? newItem?.invoiceId,
          customerId: customerId ?? newItem?.customerId,
        };
      }) ?? [],
    [customerId, invoiceId, items],
  );

  const addItems = useCallback(
    () =>
      api.post(`/service-proxy/broker/invoice-actions`, {
        actions: payload,
        type: 'ADD_ITEM',
      }),
    [payload],
  );

  const uploadModelData = useMemo(() => getModelData(tInvoices), [tInvoices]);

  const uploadModelColumns = useMemo(() => {
    const columns = getModelDataColumns(tInvoices);
    if (invoiceId && customerId) {
      columns.shift();
      columns.shift();
    }
    if (stepModal === 'upload') {
      columns.pop();
    }
    return columns;
  }, [tInvoices, stepModal, invoiceId, customerId]);

  const handleClose = useCallback(() => {
    onClose();
    setItems(undefined);
  }, [onClose]);

  const info = useCallback(() => {
    Modal.info({
      title: t('csv-format-example'),
      width: 700,
      content: (
        <>
          <Table
            columns={uploadModelColumns}
            dataSource={uploadModelData}
            pagination={false}
            size="small"
            tableLayout="auto"
            bordered
            style={{ margin: '12px 0' }}
          />
          <CsvExport
            columns={uploadModelColumns}
            data={uploadModelData}
            filename="add-items-model-import.csv"
            separator=","
          >
            <Button
              className="button-download"
              icon={<DownloadOutlined />}
              size="large"
              type="link"
            >
              {t('download-model')}
            </Button>
          </CsvExport>
        </>
      ),
    });
  }, [t, uploadModelColumns, uploadModelData]);

  const { navigateToActionsMsg } = useNavigateToActionsMsg();

  const handleAddItems = useCallback(async () => {
    try {
      const { data } = await addItems();
      handleClose();
      navigateToActionsMsg(data.operationProtocol);
    } catch {
      message.error(tInvoices('invoiceItem.msgs.error-to-creating'));
    }
  }, [addItems, handleClose, navigateToActionsMsg, tInvoices]);

  const AddItemsConfirmation = useCallback(() => {
    Modal.info({
      title: tInvoices('items-modal.confirmation.count', {
        count: items?.length,
      }),
      width: 440,
      closable: true,
      content: (
        <Paragraph>{tInvoices('items-modal.confirmation.paragraph')}</Paragraph>
      ),
      onOk: handleAddItems,
      okText: tInvoices('items-modal.buttons.confirm'),
    });
  }, [handleAddItems, items, tInvoices]);

  const handleTabsChange = useCallback((tab: any) => {
    setStepModal(tab);
    setItems(undefined);
  }, []);

  const tabItems = [
    {
      key: 'manually',
      label: tInvoices('items-modal.steps.manually'),
      children: (
        <EditableTable
          allowDelete
          allowAddRows
          columns={uploadModelColumns}
          data={items ?? []}
          onChange={setItems}
        />
      ),
    },
    {
      key: 'upload',
      label: tInvoices('items-modal.steps.upload'),
      children: (
        <>
          <Text>
            {tInvoices('items-modal.select-items')}
            <Tooltip placement="right" title={t('help')}>
              <Button
                type="link"
                icon={<QuestionCircleOutlined />}
                onClick={info}
              />
            </Tooltip>
          </Text>
          <FileUploader
            style={{
              marginTop: 16,
            }}
            uploadColumns={uploadModelColumns}
            onUpload={values => setItems(values)}
            filesListState={[uploadedFile, setUploadedFile]}
          />
        </>
      ),
    },
  ];

  return (
    <Modal
      bodyStyle={{
        paddingTop: 0,
      }}
      footer={
        <>
          <Button onClick={handleClose}>
            {tInvoices('items-modal.buttons.cancel')}
          </Button>
          <Button
            type="primary"
            disabled={!items || items?.length === 0}
            onClick={AddItemsConfirmation}
          >
            {tInvoices('items-modal.buttons.next')}
          </Button>
        </>
      }
      maskClosable={false}
      okButtonProps={{ form: 'customerForm', htmlType: 'submit' }}
      style={{
        maxWidth: 900,
      }}
      title={tInvoices(`items-modal.new-items${invoiceId ? '' : '-in-list'}`)}
      open={visible}
      width="70%"
      centered
      onCancel={handleClose}
      afterClose={() => document.body.style.removeProperty('overflow')}
    >
      <S.TabsContainer
        items={tabItems}
        onChange={handleTabsChange}
        defaultActiveKey={stepModal}
      />
    </Modal>
  );
};

export default AddItemsToInvoice;
